import { useMemo } from "react";
import { TotalCostEntities } from "./components/constants";

// Diff values below this round to floating-point noise and shouldn't show as
// a ghost `other` row.
const NOISE_THRESHOLD = 0.005;

type ServerlessRow = Record<string, number | null | undefined>;

const sumKeys = (row: ServerlessRow, keys: readonly string[]): number =>
  keys.reduce((s, k) => {
    const v = Number(row[k]);
    return s + (Number.isFinite(v) ? v : 0);
  }, 0);

const derive = (total: number | null | undefined, knownSum: number): number | null => {
  // Missing total → null so the row hides (we don't know what `other` is).
  if (total == null) return null;
  const n = Number(total);
  if (!Number.isFinite(n)) return null;
  const diff = n - knownSum;
  // Above threshold: real residual. At/below threshold (including negative
  // BE/FE drift): clamp to 0 so the `other` row still renders as `$0`,
  // making it obvious the math reconciles.
  return diff > NOISE_THRESHOLD ? diff : 0;
};

/**
 * Derives the `other` row for the serverless cost breakdown:
 * `other = total − sum(surfaced top-level categories)`.
 *
 * Snowflake's serverless billing has 27 categories; the chart surfaces 8 as
 * top-level rows. The backend's own `other` field captures values that fell
 * outside ServerlessBreakdownList — almost always 0 because the query
 * pre-filters to known categories. This hook's derived `other` absorbs that
 * plus every tail category not surfaced as its own row, so it equals
 * `backend.other + sum(unsurfaced_categories)`.
 *
 * The top-level keys are derived from `TotalCostEntities.serverless.fields`
 * (skipping the total row and the `other` row itself), so adding a category
 * in constants.ts automatically updates the math here.
 *
 * When the cost type isn't serverless, returns the input rows unchanged.
 */
export const useServerlessOtherCategory = <T extends ServerlessRow>(
  rows: T[] | undefined,
  costType: string,
  totalCostEntities: ReturnType<typeof TotalCostEntities>
): T[] | undefined => {
  const topLevelKeys = useMemo(
    () =>
      totalCostEntities.serverless?.fields
        .filter(f => !f.isTotal && f.label !== "other")
        .map(f => f.key.current) ?? [],
    [totalCostEntities]
  );

  return useMemo(() => {
    if (costType !== "serverless" || !rows) {
      return rows;
    }
    return rows.map(item => {
      const knownSum = sumKeys(item, topLevelKeys);
      const knownSumEst = sumKeys(
        item,
        topLevelKeys.map(k => `${k}_estimate`)
      );
      return {
        ...item,
        other: derive(item.total, knownSum),
        other_estimate: derive(item.total_estimate, knownSumEst),
      };
    });
  }, [costType, rows, topLevelKeys]);
};
